import React, { useCallback, useEffect, useMemo, useRef } from 'react';

// DEPENDENCIES
import clsx from 'clsx';
import get from 'lodash/get';
import { compose } from 'redux';
import { useFormikContext } from 'formik';
import { Dropdown } from 'react-bootstrap';

// GLOBAL FUNCTIONS
import { controlName, doCallback } from 'functions.js';

// HELPERS
import { inputGroup } from '../../../helpers/layout/inputGroup';
import { inputGroupAddons } from '../../../helpers/layout/inputGroupAddons';

// CORE COMPONENTS
import Button from 'core/tools/Button';

// LOCAL COMPONENTS
import Placeholder from '../parts/Placeholder';

// STYLES
import styles from './applyRange.module.scss';

// MAIN COMPONENT
export const applyRange = Component => {
  return ({
    endDate, //
    endName,
    name,
    onDateSelect,
    size,
    startDate,
    startName,
    useApply,
    useOptions,
    ...props
  }) => {
    // REFS
    const dropdown = useRef();
    const dateRange = useRef({ initialStartDate: startDate, initialEndDate: endDate });

    // HOOKS
    const {
      errors, //
      setFieldValue,
      validateField,
      validateForm,
      values
    } = useFormikContext() || {};

    // MEMOS
    const startDateName = useMemo(() => {
      return name ? controlName('start', 'dummy', name) : startName;
    }, [name, startName]);

    const endDateName = useMemo(() => {
      return name ? controlName('end', 'dummy', name) : endName;
    }, [name, endName]);

    const startDateError = useMemo(() => {
      return get(errors, startDateName);
    }, [errors, startDateName]);

    const endDateError = useMemo(() => {
      return get(errors, endDateName);
    }, [errors, endDateName]);

    // CALLBACKS
    const handleToggled = useCallback(
      isOpen => {
        // Reset values/errors when closed
        if (!isOpen) {
          doCallback(setFieldValue, startDateName, dateRange.current.initialStartDate);
          doCallback(setFieldValue, endDateName, dateRange.current.initialEndDate);
          validateField(startDateName);
          validateField(endDateName);
        }
      },
      [setFieldValue, startDateName, endDateName, validateField]
    );

    const handleApply = useCallback(() => {
      const startDateValue = get(values, startDateName) || '';
      const endDateValue = get(values, endDateName) || '';
      onDateSelect(`${startDateValue}-${endDateValue}`);
      dateRange.current = { initialStartDate: startDateValue, initialEndDate: endDateValue };
    }, [values, startDateName, endDateName, onDateSelect]);

    // EFFECTS
    // If one field fixes error in the other
    useEffect(() => {
      validateField(startDateName);
      validateField(endDateName);
    }, [startDateError, endDateError, startDateName, endDateName, validateField]);

    const { initialStartDate, initialEndDate } = dateRange.current;

    // RENDER
    return useApply ? (
      <Dropdown
        className={clsx('w-auto date-dropdown', styles.dropdown, useOptions && styles.options)} //
        onToggle={handleToggled}
        ref={dropdown}
      >
        <Dropdown.Toggle
          as={compose(
            inputGroup,
            inputGroupAddons
          )(({ children, ...p }) => (
            <span {...p} className={clsx('form-control', styles.toggle)}>
              {children}
            </span>
          ))}
          append={{
            use: 'chevron-down',
            className: clsx(styles.caret)
          }}
          id={`daterange-toggle-${name}`}
          size={size}
        >
          {initialStartDate || <Placeholder />} - {initialEndDate || <Placeholder />}
        </Dropdown.Toggle>
        <Dropdown.Menu className={clsx(styles.menu, 'py-1 shadow')}>
          <Component
            {...props}
            endDate={get(values, endDateName) || endDate}
            endDateError={endDateError}
            endName={endDateName}
            onStartDateSelect={v => setFieldValue(startDateName, v, true)}
            onEndDateSelect={v => setFieldValue(endDateName, v, true)}
            startDate={get(values, startDateName) || startDate}
            startDateError={startDateError}
            startName={startDateName}
            showArrow
          />
          <div className={styles.buttons}>
            <Button
              size="sm"
              onClick={async () => {
                const validationErrors = await validateForm();
                const isValid = !get(validationErrors, startDateName) && !get(validationErrors, endDateName);
                if (isValid) {
                  handleApply();
                  dropdown.current.click();
                }
              }}
            >
              Apply
            </Button>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    ) : (
      <Component
        {...props}
        endDate={endDate}
        endName={endDateName}
        onStartDateSelect={v => onDateSelect(v, 0)}
        onEndDateSelect={v => onDateSelect(v, 1)}
        startDate={startDate}
        startName={startDateName}
      />
    );
  };
};
